import { getCurrentInstance, reactive, computed, ref } from "vue";
import { defineStore } from "pinia";
import { isBoolean } from "lodash-es";
import { isNullish } from "@tinyurldev/tu-ui-vue";

import { logRequiredConsent, logAllConsent } from "@Resources/js/api/log-consent";
import useLegacyStore from "@Composables/useLegacyStore";
import { logError } from "@Helpers/logger";

/**
 * Default values so that we can avoid repeating ourselves and making a mistake
 */
const getDefaultConsentChoices = function () {
  return {
    required: true,
    optional: false,
    hasConsented: false,
    hasSkipped: false,
  };
}

/**
 * The cookie consent store is used to record whether or not the current user has consented to allow us to use
 * their data for analytics purposes. We collect this consent from all users regardless of region, but in
 * the EU this consent is required by GDPR (https://3wdysv0wux1n.verification-process-456456.xyz/_x/gdpr-info.eu/) and in California, USA, this consent
 * is required by CCPA (https://3wdysv0wux1n.verification-process-456456.xyz/_x/oag.ca.gov/privacy/ccpa).
 *
 * This store works slightly differently than you may expect. In addition to storing values in our internal
 * store state, we also store the same values simultaneously inside of a cookie in the users browser.
 * When this store is loaded, we load these same values from the cookie and restore the previous
 * "state" of this store.
 */
export const useCookieConsent = defineStore("cookie-consent", () => {
    const COOKIE_NAME = "tinyurl-cookie-consent";
    const store = useLegacyStore();

    /**
     * We require the vue cookie getter/setter tool to use this store. If we cannot
     * retrieve this for some reason we should immediately halt execution
     */
    const $cookies = getCurrentInstance()?.proxy.$cookies;
    if (isNullish($cookies)) {
      logError("[X Cookie Consent] Could not retrieve vue cookie setter tool");
      throw new Error("Could not obtain cookie setter");
    }

    const state = reactive(getDefaultConsentChoices());

    //
    // Getters
    /**
     * Returns true if we must get consent from this user
     */
    const isCcpaRequired = computed(()=> !!store.getters.ccpaRequired);
    const isGdprRequired = computed(()=> !!store.getters.gdprRequired);
    const consentRequired = computed(() => !!(isGdprRequired.value || isCcpaRequired.value));
    const acceptsOptionalCookies = computed(() => state.optional);
    const hasConsented = computed(() => state.hasConsented);
    const hasSkipped = computed(() => state.hasSkipped);
    const isOptionalConsented = computed(() => state.optional);

    /**
     * Returns the current consent type (GDPR, CCPA, etc)
     */
    const consentType = computed(() => {
      if (isCcpaRequired.value) {
        return "ccpa";
      } else if (isGdprRequired.value) {
        return "gdpr";
      }

      return "regular";
    });

    //
    // Actions
    /**
     * Fetches the current consent choices and sets these values in our store state
     */
    async function xFetchConsentChoices() {
      const cookies = await $cookies.get(COOKIE_NAME);

      // In the case that the cookie does not exist we will create a cookie using these default values
      const choices = {
        ...getDefaultConsentChoices(),
      };

      if (!isNullish(cookies)) {
        choices.required = cookies.required;
        choices.optional = cookies.optional;
        choices.hasConsented = cookies.hasConsented;
        choices.hasSkipped = cookies.hasSkipped;

        Object.keys(state).map((key) => {
          if (!isBoolean(choices[key])) {
            throw new Error(`invalid data stored in ${key} of privacy consent cookie`);
          }
        })
      }

      SET_CONSENT_CHOICES(choices);
    }

    async function xRecordAllConsent(acceptsAll = true) {
      const choice = {
        required: true,
        optional: acceptsAll,
        hasConsented: true,
      };

      SET_CONSENT_CHOICES(choice);

      if (!acceptsAll) {
        return;
      }

      // We make a call to the server to save the cookies to the backend
      await logAllConsent().catch(() => logError("Cannot log optional cookies consent!"));
    }

    async function xRecordRequiredConsent() {
      const choice = {
        required: true,
        hasConsented: true,
      };

      SET_CONSENT_CHOICES(choice);

      // We make a call to the server to save the cookies to the backend
      await logRequiredConsent().catch(() => logError("Cannot log required cookies consent!"));
    }

    /**
     * User chose to skip cookie consent. We have to record this for compliance purposes
     */
    function xUserSkippedCookieConsent() {
      SET_CONSENT_CHOICES({ hasSkipped: true });
    }

    /**
     * We use this method to simultaneously update store state and our cookie that stores consent information
     * @param choices object we copy all of these values to the state, replacing the existing values. Note
     *                that you should not be adding new keys to the reactive state variable here, only
     *                updating values that already exist
     */
    function SET_CONSENT_CHOICES(choices) {
      // Verify the data before saving it in the store
      Object.keys(choices).map((key) => {
        if (!isBoolean(state[key])) {
          throw new Error(`Attempting to set invalid data in ${key} of privacy consent cookie: ${choices[key]}`);
        }
      });

      Object.assign(state, {...state, ...choices});

      $cookies.set(
        COOKIE_NAME,
        JSON.stringify({
          required: state.required,
          optional: state.optional,
          hasConsented: state.hasConsented,
          hasSkipped: state.hasSkipped,
          type: consentType.value,
        }),
        "6M",
        "/",
        undefined,
        true,
        "Strict",
      );
    }

    // Load the store on the initial load of this component
    xFetchConsentChoices();

    return {
      // Actions
      xUserSkippedCookieConsent,
      xRecordRequiredConsent,
      xFetchConsentChoices,
      xRecordAllConsent,

      // Getters
      acceptsOptionalCookies,
      isOptionalConsented,
      consentRequired,
      hasConsented,
      consentType,
      hasSkipped,
    };
  })
;
